import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, Star, ArrowLeft, Tag, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import ShopHeader from "@/components/ShopHeader";

const disc = (p) => (p.mrp && p.price && p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0);

export default function DealsPage() {
  const nav = useNavigate();
  const [all, setAll] = useState([]);
  const [offers, setOffers] = useState([]);
  const [ext, setExt] = useState({ enabled: false, deals: [] });
  const origin = process.env.REACT_APP_BACKEND_URL;
  const [minRating, setMinRating] = useState(0);
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("discount");

  useEffect(() => {
    document.title = "Today's Deals — Suffle Online";
    api.get("/marketplace/public/products").then((r) => setAll(r.data.products || [])).catch(() => setAll([]));
    api.get("/marketplace/public/promotions/active").then((r) => setOffers(r.data.promotions || [])).catch(() => setOffers([]));
    api.get("/marketplace/public/external-deals").then((r) => setExt(r.data || { enabled: false, deals: [] })).catch(() => setExt({ enabled: false, deals: [] }));
  }, []);

  const offerLabel = (o) => o.discount_type === "free_shipping" ? "Free shipping" : o.discount_type === "percentage" ? `${o.discount_value}% off` : o.discount_type === "fixed" ? `₹${o.discount_value} off` : (o.name || "Offer");

  const items = useMemo(() => {
    let x = all.filter((p) => disc(p) > 0);
    if (minRating) x = x.filter((p) => (p.rating_avg || 0) >= minRating);
    if (maxPrice) x = x.filter((p) => (p.price || 0) <= Number(maxPrice));
    if (sort === "discount") x = [...x].sort((a, b) => disc(b) - disc(a));
    else if (sort === "price_asc") x = [...x].sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") x = [...x].sort((a, b) => b.price - a.price);
    else if (sort === "rating") x = [...x].sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0));
    return x;
  }, [all, minRating, maxPrice, sort]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="deals-page">
      <ShopHeader />
      <div className="mx-auto flex max-w-[1400px] items-center gap-2 px-4 pt-6 lg:px-8"><span className="inline-flex items-center gap-1.5 text-lg font-bold text-slate-900"><Tag className="h-5 w-5 text-fuchsia-500" /> Today's Deals</span></div>

      <main className="mx-auto max-w-[1400px] px-4 py-6 lg:px-8">
        {offers.length > 0 && (
          <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="active-offers">
            <span className="text-xs font-semibold text-slate-500">Active offers:</span>
            {offers.map((o) => (
              <span key={o.promo_id} data-testid={`offer-${o.promo_id}`} className="inline-flex items-center gap-1 rounded-full bg-fuchsia-50 px-3 py-1 text-xs font-medium text-fuchsia-700 ring-1 ring-fuchsia-200">
                <Tag className="h-3 w-3" />{offerLabel(o)}{o.new_customer ? " · New customers" : ""}{o.min_order_value ? ` · min ₹${o.min_order_value}` : ""}
              </span>
            ))}
          </div>
        )}
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3" data-testid="deals-filters">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-slate-500">Min rating</span>
            {[0, 3, 4].map((r) => (
              <button key={r} onClick={() => setMinRating(r)} data-testid={`deals-rating-${r}`} className={`rounded-full px-3 py-1 text-xs font-medium ${minRating === r ? "brand-gradient-bg text-white" : "border border-slate-200 text-slate-600"}`}>{r === 0 ? "Any" : `${r}★+`}</button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-slate-500">Max price</span>
            <input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="₹" data-testid="deals-max-price" className="h-8 w-24 rounded-lg border border-slate-200 px-2 text-sm outline-none focus:border-blue-400" />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="deals-sort" className="ml-auto h-8 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-600">
            <option value="discount">Biggest discount</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="rating">Top rated</option>
          </select>
        </div>

        {items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="deals-empty">
            <Tag className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-semibold text-slate-700">No deals match your filters</p>
            <p className="mt-1 text-sm text-slate-500">Try widening the filters — we only show genuine discounts from verified sellers.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="deals-grid">
            {items.map((p) => (
              <div key={p.product_id} onClick={() => nav(`/product/${p.slug || p.product_id}`)} data-testid={`deal-${p.product_id}`} className="cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:shadow-md">
                <div className="relative aspect-square bg-slate-100">
                  {(p.images || [])[0] || p.image ? <img src={(p.images || [])[0] || p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Package className="h-8 w-8 text-slate-300" /></div>}
                  <span className="absolute left-2 top-2 rounded-full bg-fuchsia-600 px-2 py-0.5 text-[10px] font-bold text-white">{disc(p)}% OFF</span>
                </div>
                <div className="p-3">
                  {p.brand && <p className="truncate text-[10px] uppercase text-slate-400">{p.brand}</p>}
                  <p className="line-clamp-2 text-sm font-medium">{p.name}</p>
                  <div className="mt-1 flex items-baseline gap-1"><span className="font-bold">₹{Math.round(p.price || 0)}</span><span className="text-xs text-slate-400 line-through">₹{Math.round(p.mrp)}</span></div>
                  {(p.rating_avg || p.rating) ? <p className="mt-1 flex items-center gap-1 text-xs text-amber-500"><Star className="h-3 w-3 fill-amber-400" /> {p.rating_avg || p.rating}{p.rating_count ? ` (${p.rating_count})` : ""}</p> : null}
                </div>
              </div>
            ))}
          </div>
        )}

        {ext.enabled && ext.deals.length > 0 && (
          <section className="mt-10" data-testid="external-deals">
            <div className="mb-3 flex items-center gap-2">
              <span className="text-lg font-bold text-slate-900">Sponsored deals from around the web</span>
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Sponsored</span>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {ext.deals.map((d) => (
                <a key={d.id} href={`${origin}${d.url}`} target="_blank" rel="sponsored nofollow noopener" data-testid={`ext-deal-${d.id}`} className="rounded-2xl border border-slate-200 bg-white p-4 transition hover:shadow-md">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs font-semibold text-fuchsia-600">{d.merchant || "Partner"}</span>
                    {d.coupon_code && <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-[10px] text-slate-600">{d.coupon_code}</span>}
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm font-medium text-slate-800">{d.title}</p>
                  {d.description && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{d.description}</p>}
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-600">Get deal <ExternalLink className="h-3 w-3" /></span>
                </a>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-slate-400">Affiliate offers from partner networks. Suffle may earn a commission. Links are marked sponsored/nofollow.</p>
          </section>
        )}
      </main>
    </div>
  );
}
